/**
 * The bottom drawer: this workflow's runs, and one run step by step.
 * Selecting a run colours the canvas; selecting a step focuses its node.
 */

import { useState } from 'react'
import { relativeTime } from '@/shared/components'
import { useRun, useWorkflowRuns } from '../api'
import type { Run, RunSummary } from '../types'

interface Props {
  workflowId: string
  activeRunId: string | null
  onPickRun: (runId: string | null) => void
  onPickNode: (nodeId: string) => void
  latest?: Run | null
}

export function RunPanel({ workflowId, activeRunId, onPickRun, onPickNode, latest }: Props) {
  const runs = useWorkflowRuns(workflowId, true)
  const detail = useRun(activeRunId && activeRunId !== latest?.run_id ? activeRunId : undefined)
  const run: Run | null | undefined = activeRunId === latest?.run_id ? latest : detail.data?.run
  const [showInput, setShowInput] = useState(false)

  return (
    <div className="wf-runs">
      <div className="wf-runs-list">
        {(runs.data?.runs ?? []).length === 0 && <p className="muted small" style={{ padding: '8px 12px' }}>No runs yet. Press Run to try the workflow.</p>}
        {(runs.data?.runs ?? []).map((r: RunSummary) => (
          <button
            key={r.run_id}
            type="button"
            className="wf-run-row"
            data-on={r.run_id === activeRunId || undefined}
            onClick={() => onPickRun(r.run_id === activeRunId ? null : r.run_id)}
          >
            <span className={`pill ${pillFor(r.status)}`}>{r.status}</span>
            <span className="wf-run-meta">
              <span>{r.trigger}</span>
              <span className="muted">{relativeTime(r.started_at)}</span>
            </span>
            <span className="mono small muted">{r.duration_ms} ms</span>
          </button>
        ))}
      </div>

      <div className="wf-run-detail">
        {!run ? (
          <p className="muted small" style={{ padding: 12 }}>Pick a run to read it step by step.</p>
        ) : (
          <>
            <div className="wf-run-summary">
              <span className={`pill ${pillFor(run.status)}`}>{run.status}</span>
              <span className="small">{run.step_count} steps</span>
              <span className="small muted">{run.duration_ms} ms</span>
              <span className="mono small muted">{run.run_id.slice(0, 8)}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowInput((v) => !v)}>
                {showInput ? 'Hide input' : 'Show input'}
              </button>
            </div>
            {run.error && <p className="field-error" style={{ padding: '0 12px' }}>{run.error}</p>}
            {showInput && <pre className="json wf-json" style={{ margin: '0 12px 8px' }}>{JSON.stringify(run.input, null, 2)}</pre>}
            <ol className="wf-steps">
              {run.steps.map((s, i) => (
                <li key={`${s.node_id}-${i}`}>
                  <button type="button" className="wf-step" data-status={s.status} onClick={() => onPickNode(s.node_id)}>
                    <span className="wf-step-n mono">{i + 1}</span>
                    <span className="wf-step-name">
                      {s.name}
                      {s.iteration != null && <span className="muted"> · iteration {s.iteration}</span>}
                    </span>
                    <span className="wf-step-counts mono small">
                      {s.input_count} in → {Object.entries(s.output_counts).map(([p, n]) => `${p} ${n}`).join(', ') || (s.error ? 'error' : '0')}
                    </span>
                    <span className="mono small muted">{s.duration_ms} ms</span>
                  </button>
                </li>
              ))}
            </ol>
            {(run.logs ?? []).length > 0 && (
              <div className="wf-logs">
                {run.logs!.map((l, i) => (
                  <div key={i} className="mono small">{l}</div>
                ))}
              </div>
            )}
            <div className="wf-run-output">
              <span className="field-label">Output</span>
              <pre className="json wf-json">{JSON.stringify(run.output, null, 2)}</pre>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function pillFor(status: string): string {
  if (status === 'success') return 'pill-success'
  if (status === 'error') return 'pill-danger'
  if (status === 'running' || status === 'queued') return 'pill-warning'
  return ''
}
